import { memo } from 'react';
import { useInView } from 'react-intersection-observer';

import { WorkspaceAgentContextSourceFieldValuePillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentContextSourceFieldValuePillChip';
import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { useWorkspaceAgentComposerWorkspaceId } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/WorkspaceAgentComposerWorkspaceProvider';
import { toWorkspaceAgentContextSourceFieldValueMentionItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentContextSourceFieldValueMentionItem';
import { type WorkspaceAgentContextSourceFieldValuePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { usePrivateContextSourcesGet } from '~/components/v2/hooks/queries/usePrivateContextSourcesGet';
import { findContextSourceFieldForValueId } from '~/components/v2/utils/context-sources/findContextSourceFieldForValueId';

export type PrivateWorkspaceAgentContextSourceFieldValuePillChipProps = {
  pill: WorkspaceAgentContextSourceFieldValuePill;
};

/**
 * A brand kit image on the history surface. The pill carries only the kit and the value, so the chip
 * re-resolves both from `ContextSources.get` — one request per kit, shared by every chip in the
 * thread that names it, and gated on `useInView` so a long history costs only the kits a reader
 * scrolls to. There is no preview card yet: the chip is the still and the label, which is all a kit
 * value has to say about itself (a card is a WORK-500 follow-on).
 */
export const PrivateWorkspaceAgentContextSourceFieldValuePillChip = memo(
  ({ pill }: PrivateWorkspaceAgentContextSourceFieldValuePillChipProps) => {
    const { contextSourceFieldValueId, contextSourceId } = pill;
    const workspaceId = useWorkspaceAgentComposerWorkspaceId();
    const { inView, ref } = useInView({ triggerOnce: true });

    const {
      data: contextSource,
      isError,
      isLoading,
    } = usePrivateContextSourcesGet(
      { contextSourceId, workspaceId: workspaceId ?? '' },
      { enabled: inView && Boolean(workspaceId) },
    );

    const field = findContextSourceFieldForValueId(contextSource, contextSourceFieldValueId);
    const value = field?.values.find((candidate) => candidate.id === contextSourceFieldValueId);
    // The picker's own mapper, so a chip draws exactly what the row the user picked drew — and
    // refuses the same values it refuses, which is what makes a converted kit value read as removed.
    const item =
      field && value ? toWorkspaceAgentContextSourceFieldValueMentionItem({ contextSourceId, field, value }) : null;

    let chip = <WorkspaceAgentPendingPill label="Brand kit image" />;

    if (item) {
      chip = <WorkspaceAgentContextSourceFieldValuePillChip thumbnailUrl={item.thumbnailUrl} title={item.title} />;
    } else if (!isLoading && (isError || contextSource)) {
      // Settled: the kit is gone or unreadable, or it no longer holds this value. Both are removed.
      chip = <WorkspaceAgentUnavailablePill kind="brandKitImage" referenceId={contextSourceFieldValueId} />;
    }

    // The observer needs a node, and every branch above is an atom with no ref to hand out.
    return (
      <span
        className="inline-flex max-w-full items-center align-middle leading-none"
        data-testid="WORKSPACE_AGENT_MESSAGE_CONTEXT_SOURCE_FIELD_VALUE_PILL"
        ref={ref}
      >
        {chip}
      </span>
    );
  },
);

PrivateWorkspaceAgentContextSourceFieldValuePillChip.displayName =
  'PrivateWorkspaceAgentContextSourceFieldValuePillChip';
